import React from 'react'
import { useApp } from '../../context/AppContext'
import { Service, api } from '../../api/client'
import type { Discount } from '../../types/app'
import { formatDay } from '../../catalog/format'
import Panel, { PanelStatus } from './Panel'
import OfferSheet from './OfferSheet'
import { offerSummary, usesLabel } from './offerForm'
import { showToast } from './toast'

const GROUPS: { state: Discount['state']; title: string }[] = [
  { state: 'live', title: 'Live' },
  { state: 'scheduled', title: 'Scheduled' },
  { state: 'ended', title: 'Ended' },
]

function dates(d: Discount): string {
  if (d.ended_at) {
    return `From ${formatDay(d.starts_on)}, ended ${formatDay(d.ended_at)}`
  }
  return d.ends_on ? `${formatDay(d.starts_on)} – ${formatDay(d.ends_on)}` : `From ${formatDay(d.starts_on)}`
}

export default function OffersPanel() {
  const { dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [offers, setOffers] = React.useState<Discount[]>([])
  const [services, setServices] = React.useState<Service[]>([])
  const [editing, setEditing] = React.useState<Discount | 'new' | null>(null)

  const fetchAll = () => Promise.all([api.listDiscounts(), api.listServices()])

  const load = React.useCallback(async () => {
    setStatus('loading')
    try {
      const [loadedOffers, loadedServices] = await fetchAll()
      setOffers(loadedOffers)
      setServices(loadedServices)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const refresh = React.useCallback(async () => {
    try {
      const [loadedOffers, loadedServices] = await fetchAll()
      setOffers(loadedOffers)
      setServices(loadedServices)
    } catch {
      // Keep showing the list we have; the next open loads it again.
    }
  }, [])

  const open = (target: Discount | 'new') => {
    setEditing(target)
    refresh()
  }

  const saved = (message: string) => {
    setEditing(null)
    refresh()
    showToast(dispatch, message)
  }

  return (
    <>
      <Panel title="Offers" intro="Sales that apply themselves, and codes clients type when they book.">
        {status !== 'ready' ? (
          <PanelStatus status={status} onRetry={load} />
        ) : (
          <>
            {offers.length === 0 && <p className="settings-status">No offers yet. Add your first one below.</p>}
            {GROUPS.map((group) => {
              const rows = offers.filter((o) => o.state === group.state)
              if (rows.length === 0) {
                return null
              }
              return (
                <div key={group.state}>
                  <h3 className="settings-section-label">{group.title}</h3>
                  <ul className="service-list">
                    {rows.map((offer) => {
                      const uses = usesLabel(offer)
                      return (
                        <li key={offer.id} className="service-item">
                          <button type="button" className="service-item__main" onClick={() => open(offer)}>
                            <span className="service-item__name">{offer.name}</span>
                            <span className="service-item__meta">
                              {offerSummary(offer)}
                              {offer.code ? ` · ${offer.code}` : ''} · {dates(offer)}
                              {uses ? ` · ${uses}` : ''}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
            <button type="button" className="btn btn-primary btn-block" onClick={() => open('new')}>
              Add offer
            </button>
          </>
        )}
      </Panel>
      {editing && (
        <OfferSheet
          offer={editing === 'new' ? null : editing}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
    </>
  )
}
